import type { ReactNode } from 'react';
import { useT } from '../i18n/locale-context';
import type { SavedSearch } from '../lib/saved-search-client';

/**
 * Which alert to tune — or whether to create another.
 *
 * Appears only when there is a genuine choice: several alerts, or room under
 * the reader's cap to add one more (paid = unlimited; free = 3 since
 * 2026-10-01, was 1). With exactly one alert and no room it never renders —
 * the builder opens directly.
 *
 * `onCreateNew` is optional: AT the cap the caller omits it, and passes the
 * limit line as `children` instead, so the reader sees why there is no
 * «Создать новое» rather than meeting a 403.
 */
export function AlertPicker({
  searches,
  onPick,
  onCreateNew,
  children,
}: {
  searches: SavedSearch[];
  onPick: (s: SavedSearch) => void;
  onCreateNew?: () => void;
  children?: ReactNode;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] font-semibold text-ink">{t('alert.picker.title')}</p>
      <ul className="flex flex-col gap-1">
        {searches.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => onPick(s)}
              className="w-full rounded-md border border-edge bg-bone-surface px-3 py-2 text-left text-[13.5px] font-medium text-ink-soft transition-colors hover:border-brand hover:text-brand"
            >
              {s.name}
            </button>
          </li>
        ))}
      </ul>
      {onCreateNew && (
        <button
          type="button"
          onClick={onCreateNew}
          className="self-start text-[12.5px] font-semibold text-brand hover:underline"
        >
          {t('alert.picker.createNew')}
        </button>
      )}
      {children}
    </div>
  );
}
